import {Directive, ElementRef, Inject, Input, OnDestroy, OnInit, Optional} from '@angular/core';
import {TableComponent} from '@blocks/table/table.component';
import {BLOCKS_CONFIG, BlocksConfig} from '@blocks/blocks.config';
import {Subscription} from 'rxjs';
import {BlockStore} from '@blocks/block/block.store';
import {BlockChangeDetectionService} from '@blocks/services/block-change-detection/block-change-detection.service';
import {TableService} from '@blocks/table/table.service';
import {throttleTime} from 'rxjs/operators';

@Directive({
  selector: 'table[resizeColumns]',
  exportAs: 'resizeColumns'
})
export class TableResizeColumnsDirective implements OnInit, OnDestroy {
  colToResize: number;
  primaryNewWidth: number = null;
  secondaryNewWidth: number = null;

  @Input() resizeColumns: boolean;

  private startResizeThrottled = false;
  private startX: number;
  private currentOffset: number;
  private subs = new Subscription();
  private primaryColWidth: number;
  private secondaryColWidth: number;
  private tableWidth: number;

  constructor(
    private elementRef: ElementRef,
    private tc: TableComponent,
    private tableService: TableService,
    private blockChangeDetectionService: BlockChangeDetectionService,
    @Optional() private blockStore: BlockStore,
    @Optional() @Inject(BLOCKS_CONFIG) private blocksConfig: BlocksConfig) {
  }

  private reset() {
    this.currentOffset = null;
    this.primaryColWidth = null;
    this.secondaryColWidth = null;
    this.primaryNewWidth = null;
    this.secondaryNewWidth = null;
    this.tableWidth = null;
    this.colToResize = null;
    this.startX = null;
    this.tc.cdr.markForCheck();
  }

  ngOnInit() {
    this.subs.add(this.tableService.mouseMove$.subscribe(data => {
      if (this.colToResize != null) {
        this.currentOffset = data.clientX - this.startX;
        this.resizeCellsLocally(this.colToResize, this.currentOffset);
      }
    }));

    this.subs.add(this.tableService.mouseUp$
      .pipe(throttleTime(200)) // Safeguard to not call resizeCells two times after another
      .subscribe(data => {
        if (this.colToResize != null) {
          this.resizeCells(this.colToResize, this.currentOffset);
        }
      }));
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
  }

  /**
   * Save essential values that are needed in further event handlers.
   */
  startResize(tableWidth: number, colIndex: number, event: MouseEvent) {
    if (this.startResizeThrottled) {
      return;
    }

    this.startResizeThrottled = true;
    setTimeout(() => this.startResizeThrottled = false, 200);

    this.reset();

    // Don't start the resize if colToResize is still set.
    // Resizing while the previous resize request is still in flight would result in buggy behavior.
    if (this.colToResize != null) {
      return;
    }

    this.tableWidth = tableWidth;
    this.colToResize = colIndex;

    // Save the start position of the mouse
    this.startX = event.clientX;

    // Clear selection when starting resize
    this.tc.cellSelection = null;

    // It's ok to stop propagation here. This event is only fired on the drag indicators
    event.stopPropagation();

    // Get width of html cell
    const getCellWidth = (index: number) => {
      const cell = this.tc.cellElements
        .map(e => (e.nativeElement as HTMLElement))
        .find(e => +e.dataset.rowindex === 0 && +e.dataset.colindex === index);

      return +cell.offsetWidth;
    };

    this.primaryColWidth = getCellWidth(colIndex);
    this.secondaryColWidth = getCellWidth(colIndex + 1);

    this.primaryNewWidth = this.primaryColWidth / this.tableWidth * 100;
    this.secondaryNewWidth = this.secondaryColWidth / this.tableWidth * 100;
  }

  /**
   * Simulate the resize and save values to public variables
   */
  resizeCellsLocally(colindex: number, offset: number) {
    if (!offset || this.primaryNewWidth == null || this.secondaryNewWidth == null) {
      return;
    }

    // Calculate new widths
    const primaryNewWidth = Math.floor((this.primaryColWidth + offset) / this.tableWidth * 10000) / 100;
    const secondaryNewWidth = Math.floor((this.secondaryColWidth - offset) / this.tableWidth * 10000) / 100;

    // Don't continue if any of the new widths would be less than 10%
    if (primaryNewWidth < 10 || secondaryNewWidth < 10) {
      return;
    }

    const sum = this.tc.block.settings.colWidths.reduce((p, c, i) => p + c, 0);

    // Set the new widths
    this.primaryNewWidth = primaryNewWidth;
    this.secondaryNewWidth = secondaryNewWidth + 100 - sum;

    // Refresh template
    this.tc.cdr.markForCheck();
  }

  /**
   * Actually resize the cells & send changes to server
   */
  resizeCells(colindex: number, offset: number) {
    const secondaryColIndex = colindex + 1;
    const copyPrimaryNewWidth = this.primaryNewWidth;
    const copySecondaryNewWidth = this.secondaryNewWidth;
    this.primaryNewWidth = null;
    this.secondaryNewWidth = null;

    if (!offset) {
      this.reset();
      return;
    }

    this.tc.cellElements
      .map(cellElement => (cellElement.nativeElement as HTMLElement))
      .filter(cellElement => +cellElement.dataset.rowindex === 0)
      .forEach(cellElement => {
        this.tc.block.settings.colWidths[colindex] = cellElement.offsetWidth / this.tableWidth * 100;
      });

    this.tc.block.settings.colWidths[colindex] = copyPrimaryNewWidth;
    this.tc.block.settings.colWidths[secondaryColIndex] = copySecondaryNewWidth;

    const sum = this.tc.block.settings.colWidths.reduce((p, c, i) => p + c, 0);
    this.tc.block.settings.colWidths[this.tc.block.settings.colWidths.length - 1] += 100 - sum;

    this.subs.add(this.blockStore
      .updateBlock(this.tc.block)
      .subscribe(data => {
        this.reset();
        this.blockChangeDetectionService.setChangedBlockIds(data.updated_blocks.map(b => b.id));
      }));
  }
}

